import { ref, computed, watch, onMounted } from "vue";
import { useConversation } from "../../../Stores/Conversation";
import { useAppState } from "../../../Stores/AppState";
import { useInvitation } from "../../../Stores/Invitation";
import { useSettings } from "../../Settings/Store/Settings";
import { useRoute } from "vue-router";
import { ROUTE_NAMES } from "../../../Constants/routes";
import utilities from "../../../Helpers/utilities";
import { useAssociateStore } from "../../../Stores/Associate";
import { useWorkspaceTree } from "../../../Stores/WorkspaceTree";
import { closeWorklineFilterPanel } from "./closeWorklineFilterPanel";
import {
    closeAssociateDashboardAndOpenConversation,
    closeAssociateSettingsAndOpenConversation,
    closeDashboardAndOpenConversation,
    closeDirectoryAndOpenConversation,
    closeDirectoryInviteAndOpenConversation,
    closeMembershipAndOpenConversation,
    closeOrganizationControlsAndOpenConversation,
    closeOrganizationReportsPanelAndOpenConversation,
    closeProductivityAndOpenConversation,
    closeSeniorityPanelAndOpenConversation,
    closeSetupPanelAndOpenConversation,
    closeWorkdayControlsAndOpenConversation,
    isAssociateDashboardView,
    isAssociateSettingsView,
    isBoardOrBoardGroupMembershipView,
    isActiveMembershipSubpanelView,
    isDirectoryGroupOrTopicMembershipView,
    isDirectoryInviteSubpanelView,
    isDirectorySubpanelView,
    isItemDashboardView,
    isItemOrItemGroupMembershipView,
    isOrganizationControlsPanelView,
    isOrganizationReportsPanelView,
    isSeniorityPanelView,
    isSetupPanelView,
    isWorkdayControlsPanelView,
    isSpaceOrSpaceGroupMembershipView,
} from "./closeSubpanelAndOpenConversation";
import {useChat} from "Admin/js/Components/Chat/Store/Chat.js";

export function useWindowPopoverState() {
    // Stores
    const conversation_store = useConversation();
    const app_state_store = useAppState();
    const settings_store = useSettings();
    const route = useRoute();
    const { chatPopover } = utilities();
    const workspaceTreeStore = useWorkspaceTree();
    const invitation_store = useInvitation();
    // Core state
    const is_hidden = ref(true);
    const is_fade_in_started = ref(true);
    const show_bread_crumbs = ref(false);

    // Computed properties
    const active_conversation = computed(() => conversation_store.active);
    const isSettingsRoute = computed(() => route.name === ROUTE_NAMES.SETTINGS);

    const is_new_directory_opened = computed(
        () => app_state_store.invite_drawer?.is_new_directory_opened,
    );

    const is_team_opened = computed(
        () => app_state_store.invite_drawer?.is_team_opened,
    );

    const is_workday_opened = computed(
        () =>
            app_state_store.invite_drawer?.is_workday_opened &&
            isSettingsRoute.value &&
            settings_store.is_workday_tab_opened &&
            chatPopover.value,
    );

    const is_workday_permission_opened = computed(
        () =>
            app_state_store.invite_drawer?.is_workday_permission_opened &&
            isSettingsRoute.value &&
            settings_store.is_workday_tab_opened,
    );

    const is_manage_organization_role_opened = computed(
        () => app_state_store.is_manage_organization_role_opened,
    );

    const is_productivity_opened = computed(
        () => app_state_store.productivity_drawer?.is_productivity_opened,
    );

    const isChatOpened = computed(() => {
        return (
            Boolean(active_conversation.value) &&
            !is_team_opened.value &&
            !is_new_directory_opened.value &&
            chatPopover.value
        );
    });

    const is_conversation_hidden = computed(() => {
        return (
            is_hidden.value &&
            !is_team_opened.value &&
            !is_new_directory_opened.value
        );
    });

    // Initialize component state
    const initializeState = () => {
        if (!active_conversation.value || active_conversation.value.isFake()) {
            is_hidden.value = true;
        } else {
            is_hidden.value = false;
            is_fade_in_started.value = false;
        }
    };

    // Handle conversation changes
    const handleConversationChange = async (value) => {
        // Reset workday states
        conversation_store.toggleGetHelpNowMenuValue(false);
        app_state_store.invite_drawer.is_workday_permission_opened = false;
        app_state_store.invite_drawer.is_workday_opened = false;
        bread_crumb_pending_hide.value = false;
        show_bread_crumbs.value = false;

        closeWorklineFilterPanel();

        // Update visibility states
        is_hidden.value = false;
        is_fade_in_started.value = true;

        // Update workspace tree
        const space = workspaceTreeStore.getWorkspaceById(value.space_id);
        if (!space?.boardCategoryOrderedIds?.length) {
            await workspaceTreeStore.loadBoards(space, false, true);
        }

        const board = workspaceTreeStore.getBoardById(value.board_id);
        if (space && !board?.itemCategoryOrderedIds?.length) {
            await workspaceTreeStore.loadItems(space, board, false, true);
        }

        // Delayed state updates
        timeOutHelper(() => {
            is_hidden.value = false;
        }, 300);

        timeOutHelper(() => {
            is_fade_in_started.value = false;
        }, 600);
    };

    // Breadcrumb management
    const bread_crumb_pending_hide = ref(false);
    const can_show_bread_crumbs = computed(() => {
        const active = active_conversation.value;
        if (!active) return false;
        const is_direct_or_group =
            typeof active.isDm === "function"
                ? active.isDm() || active.isGroup?.()
                : active.is_dm || active.is_group;
        return !is_direct_or_group;
    });

    const activateBreadCrumbs = (activate = true) => {
        if (is_team_opened.value || is_new_directory_opened.value || !can_show_bread_crumbs.value) {
            bread_crumb_pending_hide.value = false;
            show_bread_crumbs.value = false;
            return;
        }

        if (!activate) {
            bread_crumb_pending_hide.value = true;
            timeOutHelper(() => {
                if (bread_crumb_pending_hide.value) {
                    show_bread_crumbs.value = false;
                }
            }, 1);
        } else {
            bread_crumb_pending_hide.value = false;
            show_bread_crumbs.value = true;
        }
    };

    // Watchers
    watch(active_conversation, handleConversationChange);

    watch(
        [
            is_team_opened,
            is_new_directory_opened,
            is_workday_opened,
            is_productivity_opened,
        ],
        ([teamOpened, newDirectoryOpened, workdayOpened, productivityOpened]) => {
            if (
                teamOpened ||
                newDirectoryOpened ||
                workdayOpened ||
                productivityOpened
            ) {
                conversation_store.toggleGetHelpNowMenuValue(false);
            }
        },
    );

    watch(chatPopover, () => {
        bread_crumb_pending_hide.value = false;
        show_bread_crumbs.value = false;
    });

    const onClose = () => {
        const associateStore = useAssociateStore();

        if (
            isDirectoryGroupOrTopicMembershipView() ||
            isItemOrItemGroupMembershipView() ||
            isBoardOrBoardGroupMembershipView() ||
            isSpaceOrSpaceGroupMembershipView()
        ) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeMembershipAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isActiveMembershipSubpanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeMembershipAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isAssociateDashboardView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                async () => {
                    if (app_state_store.show_control_panel) return;
                    await closeAssociateDashboardAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isItemDashboardView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeDashboardAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isAssociateSettingsView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                async () => {
                    if (app_state_store.show_control_panel) return;
                    await closeAssociateSettingsAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isDirectorySubpanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeDirectoryAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isDirectoryInviteSubpanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeDirectoryInviteAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isSetupPanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeSetupPanelAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isWorkdayControlsPanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeWorkdayControlsAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isOrganizationReportsPanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeOrganizationReportsPanelAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isOrganizationControlsPanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeOrganizationControlsAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (isSeniorityPanelView()) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeSeniorityPanelAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        if (is_productivity_opened.value) {
            associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(
                () => {
                    if (app_state_store.show_control_panel) return;
                    closeProductivityAndOpenConversation({
                        isHiddenRef: is_hidden,
                    });
                },
            );
            return;
        }

        associateStore.runPendingActionAfterAssociateSaveDialogIfNeeded(() => {
            useChat().setTab(null);
            if (conversation_store.isActiveQuickConversation) {
                invitation_store.resetQuickConversation();
            }
            conversation_store.closeChatPanelCompletely();
        });
    };

    onMounted(() => {
        timeOutHelper(initializeState, 100);
    });

    return {
        // State
        is_hidden,
        is_fade_in_started,
        show_bread_crumbs,

        // Computed
        active_conversation,
        is_team_opened,
        is_new_directory_opened,
        is_workday_opened,
        is_workday_permission_opened,
        is_manage_organization_role_opened,
        is_productivity_opened,
        is_conversation_hidden,
        isChatOpened,
        // Methods
        initializeState,
        activateBreadCrumbs,
        onClose,
    };
}
